"use client";

import { StarRating } from "@/components/common/StarRating";
import { ImageWithLoader } from "@/components/ui";
import {
  loading,
  hotels,
  getHotelAppliedFilters,
} from "@/lib/features/hotel/hotelSlice";
import { useAppSelector } from "@/lib/hooks";
import { getMyRoomSummary } from "@/utils/helper";
import { parseQueryString, makeQueryString } from "@/utils/queryString";
import { formatDate, set } from "date-fns";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InfoCircle } from "react-bootstrap-icons";
import Skeleton from "react-loading-skeleton";
import { useRouter, useSearchParams } from "next/navigation";
import { ModalHeader, Button } from "reactstrap";
import Link from "next/link";

// Lazy load components
const ModalComponent = lazy(() =>
  import("reactstrap").then((mod) => ({ default: mod.Modal }))
);
const CancellationPolicy = lazy(
  () => import("@/components/hotel-single/CancellationPolicy")
);
const OtherInclusions = lazy(
  () => import("@/components/common/OtherInclusions")
);

interface HotelPopupProps {
  hotelItem: any;
  onClose: () => void;
}

// Device type detection function
const getDeviceType = (): "mobile" | "tablet" | "laptop" | "desktop" | "high-res" => {
  if (typeof window === "undefined") {
    return "desktop"; // Default for SSR
  }

  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isIPad = userAgent.includes("ipad") || (userAgent.includes("macintosh") && isTouchDevice);
  const isHighRes = window.devicePixelRatio >= 2 && width >= 1440;

  if (width <= 576) {
    return "mobile"; // Phones
  } else if ((width >= 577 && width <= 991) || isIPad) {
    return "tablet"; // Tablets, iPads
  } else if (width >= 992 && width <= 1366) {
    return "laptop"; // Laptops, smaller desktops
  } else if (isHighRes) {
    return "high-res"; // High-resolution displays (e.g., Retina, 4K monitors)
  } else {
    return "desktop"; // Standard desktops
  }
};

const HotelPopup = ({ hotelItem, onClose }: HotelPopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [modal, setModal] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const isLoading = useAppSelector(loading);
  const hotelsData = hotelItem?.hotelInfo;
  const hotel = hotelItem?.hotel;
  const [deviceType, setDeviceType] = useState<"mobile" | "tablet" | "laptop" | "desktop" | "high-res">(
    getDeviceType()
  );
  const [topPosition, setTopPosition] = useState<string>("80%"); // Default to center

  // Calculate top position dynamically
  const calculateTopPosition = useCallback(() => {
    if (typeof window === "undefined" || !popupRef.current) {
      return "80%"; // Fallback for SSR or when ref is not ready
    }

    const windowHeight = window.innerHeight;
    const popupHeight = popupRef.current.offsetHeight;
    const device = getDeviceType();

    // Base top offset percentages for different devices
    const baseOffsets: Record<string, number> = {
      mobile: 45, // Closer to top on small screens
      tablet: 55, // Slightly higher on tablets
      laptop: 40, // Adjusted for laptop screens
      desktop: 50, // Centered on desktops
      "high-res": 80, // Centered on high-res displays
    };
    // console.log("baseOffsets", baseOffsets) 
    // Calculate dynamic top position
    let topPercent = baseOffsets[device] || 80;

    // console.log("topPercent", topPercent, device) 


    // Adjust based on popup height relative to window height
    /*if (popupHeight > windowHeight * 0.7) {
      topPercent = 10; // Move higher for very tall popups
    } else if (popupHeight < windowHeight * 0.3) {
      topPercent = 50; // Center for small popups
    }

    // Ensure popup stays within viewport
    const minTop = 20; // Minimum top percentage to avoid cutting off
    const maxTop = 100 - (popupHeight / windowHeight) * 100; // Ensure bottom is visible
    topPercent = Math.max(minTop, Math.min(topPercent, maxTop));
    */
    return `${topPercent}%`;
  }, []);

  // Update device type and top position on resize
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      // Debounce resize to improve performance
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setDeviceType(getDeviceType());
        setTopPosition(calculateTopPosition());
      }, 100);
    };

    handleResize(); // Initial calculation
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [calculateTopPosition]);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [onClose]);

  const payloadFromQuery = useMemo(() => {
    return searchParams ? parseQueryString(searchParams) : null;
  }, [searchParams]);

  const toggleModal = useCallback((data: any = null) => {
    setModalData(data);
    setModal((prev) => !prev);
  }, []);

  const redirectUrl = useCallback(
    (hotel_code: any, search_id: any) => {
      const payload = payloadFromQuery;
      const req: any = { ...payload, hotel_code, search_id };
      const queryString = makeQueryString(req);
     return `/hotel?${queryString}`;
    },
    [router, payloadFromQuery]
  );

  const roomSummary = useMemo(() => getMyRoomSummary(hotelsData), [hotelsData]);

  if (isLoading) {
    return (
      <div
        className="bg-white p-3 rounded shadow-lg border d-flex align-items-center justify-content-center"
        style={{ width: "90%", maxWidth: "350px" }}
      >
        <Skeleton height={200} width="100%" />
      </div>
    );
  }

  return (
    <>
      {modalData && (
        <Suspense fallback={<div>Loading...</div>}>
          <ModalComponent
            isOpen={modal}
            toggle={() => toggleModal()}
            fade={false}
          >
            <ModalHeader toggle={() => toggleModal()}>
              Cancellation Policy
            </ModalHeader>
            <div className="modal-body">
              {modalData?.non_refundable ? (
                "This booking is non-refundable and cannot be amended or modified. If you fail to arrive, no refund will be given."
              ) : (
                <Suspense fallback={<Skeleton width={300} height={20} />}>
                  <CancellationPolicy data={modalData} />
                </Suspense>
              )}
            </div>
            <div className="modal-footer">
              <Button
                color="primary"
                onClick={() => toggleModal()}
                className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
              >
                Close
              </Button>
            </div>
          </ModalComponent>
        </Suspense>
      )}
      {/* {JSON.stringify(deviceType)}
      {JSON.stringify(topPosition)} */}
      <div
        ref={popupRef}
        className="bg-white p-3 rounded shadow-lg border d-flex flex-column flex-md-row align-items-md-center position-fixed"
        style={{
          width: "90%",
          maxWidth: deviceType === "mobile" ? "350px" : "600px",
          zIndex: 1100,
          top: topPosition,
          left: "50%",
          transform: `translate(-50%, -50%)`,
          maxHeight: "80vh",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: "20px",
        }}
      >
        <button
          onClick={onClose}
          className="btn-close position-absolute top-0 end-0"
          aria-label="Close"
        />

        <div className="col-md-4 mb-3 mb-md-0">
          <div className="cardImage ratio ratio-1:1 w-100 rounded">
            <div className="cardImage__content">
              <ImageWithLoader
                width={400}
                height={400}
                src={hotel?.images?.url}
                alt={`${hotel?.name} image`}
                className="w-100"
                loading="lazy"
                parentClassName="cardImage__content"
              />
            </div>
          </div>
        </div>

        <div className="col-md-5 px-md-3 flex-grow-1">
          <h3 className="text-16 lh-16 fw-500 mb-1">
          <Link href={`${redirectUrl(hotel?.hotel_code, hotel?.search_id)}`} target="_blank">{hotel.name}</Link>
            {hotel.category && (
              <div className="mt-1">
                <StarRating value={hotel.category} />
              </div>
            )}
          </h3>

          {(hotel.city || hotel.country) && (
            <div className="d-flex gap-2 align-items-center mb-2">
              {hotel.tripadvisor_data?.ranking_data && (
                <span className="text-12 fw-500">
                  {`#${hotel.tripadvisor_data.ranking_data.ranking} in`}
                </span>
              )}
              <span className="text-12">{`${hotel.city}, ${hotel.country}`}</span>
            </div>
          )}

          {hotel.min_rate && (
            <>
              <div className="text-12 lh-15 mb-2">
                {hotel.min_rate.boarding_details?.[0] && (
                  <div className="fw-500">
                    {hotel.min_rate.boarding_details[0]}
                  </div>
                )}
                {hotel.min_rate.rooms?.[0]?.description && (
                  <div className="text-muted">
                    {hotel.min_rate.rooms[0].description}
                  </div>
                )}
              </div>

              {hotel.min_rate.non_refundable != null &&
                (hotel.min_rate.non_refundable ? (
                  <span
                    onClick={() => toggleModal(hotel.min_rate)}
                    className="text-danger text-12 cursor-pointer d-flex align-items-center gap-1"
                  >
                    Non refundable <InfoCircle />
                  </span>
                ) : (
                  <div className="text-12 text-success lh-15">
                    <div className="fw-500 d-flex align-items-center gap-1">
                      {hotel.min_rate.cancellation_policy?.under_cancellation ==
                      false
                        ? `Free cancellation till ${formatDate(
                            new Date(
                              hotel.min_rate.cancellation_policy
                                ?.cancel_by_date || ""
                            ),
                            "dd MMM yyyy HH:mm"
                          )}`
                        : "Refundable"}
                      <InfoCircle
                        onClick={() => toggleModal(hotel.min_rate)}
                        className="cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>

        <div className="col-md-3 text-md-end mt-3 mt-md-0">
          <div className="text-12 text-muted">{roomSummary}</div>
          {hotel.min_rate && (
            <>
              <div className="text-18 lh-12 fw-600 mt-1">
                {hotel.min_rate.currency} {hotel.min_rate.priceWithMarkup}
              </div>
              <div className="text-12 text-muted">
                Including taxes and charges
              </div>
            </>
          )}
          {hotel.hotel_code && hotel.search_id && (
            // <button
            //   onClick={() => redirectUrl(hotel.hotel_code, hotel.search_id)}
            //   type="button"
            //   className="button -md -dark-1 bg-blue-1 text-white mt-2 w-100"
            // >
            //   See Availability
            // </button>
             <Link href={`${redirectUrl(hotel?.hotel_code, hotel?.search_id)}`} target="_blank" className="button -md -dark-1 bg-blue-1 text-white mt-2 w-100">See Availability</Link>

          )}
        </div>
      </div>
    </>
  );
};

export default HotelPopup;